/**
 * UnifiedGeoRoleFilter — Iter-111 · "Enterprise Pivot Filter".
 *
 * PER-BOARD self-contained filter, ADMIN dashboard only (field staff get a
 * compact locked variant; client/prospect/demo pages never mount this).
 * Each department board renders it BELOW the department header, role
 * assignment and key responsibilities — the last element before board data.
 *
 *   const filter = useBoardFilter('dept-operations');
 *   <UnifiedGeoRoleFilter filter={filter} />
 *
 * Admin layout (exact row order):
 *   Row 1 — Role selector: Valet | Cleaners (exclusive, never mixed data)
 *   Row 2 — Country selector: US | Canada (default US)
 *   Row 3 — Refresh · Export CSV · Snapshot Period
 *   Row 4 — State pills (all US states; only TX operational → expands the
 *            5 TX city buttons: DALLAS HOUSTON AUSTIN SAN ANTONIO TYLER,
 *            multi-select, Statistics-section gold selection style)
 *   Row 5 — Region · City · Date From/To (+ Clear)
 *
 * Every change re-queries the board below via `queryString`/`refreshTick`.
 * State is persisted per (user, boardId) so boards stay independent.
 */
import { useCallback, useEffect, useMemo, useState, useLayoutEffect, createContext, useContext } from 'react';
import { installAdminScopeTransport, setAdminRequestScope } from '../lib/adminScopeTransport';
import { Calendar, MapPin, RotateCcw, RefreshCw, Download, Users, Lock, Globe2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { apiSafeFetch } from '../lib/apiFetch';
import { AdminPopulationMetrics } from './admin/AdminPopulationMetrics';
import './admin/AdminResponsiveTables.css';
export const AdminBoardFilterContext = createContext(null);

const SNAPSHOT_PERIODS = [
  { key: 'today',      label: 'Today',      days: 1 },
  { key: 'this_week',  label: 'This Week',  days: 7 },
  { key: 'this_month', label: 'This Month', days: 30 },
  { key: 'this_year',  label: 'This Year',  days: 365 },
  { key: 'custom',     label: 'Custom Date Range', days: null },
  // Legacy persisted keys — kept resolvable so old saved filters hydrate.
  { key: 'week',  label: 'This Week',  days: 7,   legacy: true },
  { key: 'month', label: 'This Month', days: 30,  legacy: true },
  { key: 'year',  label: 'This Year',  days: 365, legacy: true },
];

// Fixed TX city order per CEO directive.
export const TX_CITIES = ['Dallas', 'Houston', 'Austin', 'San Antonio', 'Tyler'];

const US_STATES = [
  ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
  ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
  ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
  ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
  ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
  ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
  ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
  ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
  ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
  ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
];

const ADMIN_ROLES = new Set([
  'admin', 'company_manager',
  'operations_director', 'finance_director', 'hr_director',
  'safety_director', 'legal_director', 'sales_director',
  'marketing_director', 'marketing_admin', 'customer_service_director',
  'procurement_director', 'training_director', 'it_director',
]);

const FIELD_ROLES = new Set(['manager', 'supervisor', 'crew_leader', 'agent', 'site_agent']);

const DEFAULT_STATE = {
  snapshot_period: 'this_month',
  country: 'US',
  state: 'TX',
  region: null,
  city: null,
  cities: [],
  date_from: '',
  date_to: '',
  view_as: 'corporate',
};

const storageKey = (u, boardId) => `necs.board_filter.${u?.user_id || u?.email || 'anon'}.${boardId}`;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/** Auto-derive the variant from the user's role. */
export function autoVariant(user) {
  if (!user) return 'admin';
  const role = (user.role || '').toLowerCase();
  if (ADMIN_ROLES.has(role)) return 'admin';
  if (FIELD_ROLES.has(role)) return 'field';
  return 'admin';
}

/**
 * useBoardFilter — one filter instance per (user, boardId). State +
 * persistence are scoped to the board so departments never share filters.
 */
export function useBoardFilter(boardId, opts = {}) {
  const { user } = useAuth();
  const sharedFilter = useContext(AdminBoardFilterContext);
  const [catalog, setCatalog] = useState(null);
  const [filter, setFilterState] = useState(DEFAULT_STATE);
  const [refreshTick, setRefreshTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  useLayoutEffect(() => { if (opts.sharedAdmin) setFilterState(DEFAULT_STATE); }, [boardId, opts.sharedAdmin]);

  useEffect(() => {
    if (!user) { setCatalog(null); setLoading(false); setHydrated(false); return; }
    if (sharedFilter && !opts.sharedAdmin) { setHydrated(true); return; }
    let alive = true;
    setLoading(true);
    setHydrated(false);
    let persisted = null;
    try {
      const raw = localStorage.getItem(storageKey(user, boardId));
      if (raw) persisted = JSON.parse(raw);
    } catch { /* ignore */ }
    apiSafeFetch('/api/geo/catalog').then(({ data }) => {
      if (!alive) return;
      if (data) setCatalog(data);
      const scope = data?.role_scope || {};
      const initial = {
        ...DEFAULT_STATE,
        country: opts.sharedAdmin ? 'US' : persisted?.country ?? 'US',
        state: opts.sharedAdmin ? 'TX' : persisted?.state ?? scope.state ?? 'TX',
        city: opts.sharedAdmin ? null : persisted?.city ?? scope.city ?? null,
        cities: opts.sharedAdmin ? [] : Array.isArray(persisted?.cities) ? persisted.cities : [],
        region: opts.sharedAdmin ? null : persisted?.region ?? scope.region ?? null,
        date_from: opts.sharedAdmin ? '' : persisted?.date_from ?? '',
        date_to: opts.sharedAdmin ? '' : persisted?.date_to ?? '',
        view_as: opts.sharedAdmin ? 'corporate' : persisted?.view_as ?? (opts.defaultViewAs || DEFAULT_STATE.view_as),
        snapshot_period: opts.sharedAdmin ? DEFAULT_STATE.snapshot_period : persisted?.snapshot_period ?? DEFAULT_STATE.snapshot_period,
      };
      setFilterState(initial);
      setLoading(false);
      setHydrated(true);
    });
    return () => { alive = false; };
  }, [user?.user_id, boardId, opts.defaultViewAs, opts.sharedAdmin, !!sharedFilter]);

  useEffect(() => {
    if (!user || !hydrated) return;
    try { localStorage.setItem(storageKey(user, boardId), JSON.stringify(filter)); } catch { /* ignore */ }
  }, [filter, user, boardId, hydrated]);

  const setFilter = useCallback((patch) => setFilterState((f) => ({ ...f, ...patch })), []);
  const refresh   = useCallback(() => setRefreshTick((t) => t + 1), []);
  const clear     = useCallback(() => {
    if (user) {
      try { localStorage.removeItem(storageKey(user, boardId)); } catch { /* ignore */ }
    }
    const scope = catalog?.role_scope || {};
    setFilterState({
      ...DEFAULT_STATE,
      state: opts.sharedAdmin ? 'TX' : scope.state ?? 'TX',
      city: scope.city ?? null,
      region: scope.region ?? null,
    });
  }, [user, boardId, catalog]);

  const windowDays = useMemo(() => {
    if (filter.snapshot_period === 'custom' && filter.date_from && filter.date_to) {
      const start = new Date(filter.date_from);
      const end   = new Date(filter.date_to);
      return Math.max(1, Math.round((end - start) / 86400000) + 1);
    }
    const p = SNAPSHOT_PERIODS.find((s) => s.key === filter.snapshot_period);
    return p?.days ?? 30;
  }, [filter.snapshot_period, filter.date_from, filter.date_to]);

  // Combined city selection = multi-select TX city buttons + the free City
  // dropdown. Serialized comma-joined; backends split into $in queries.
  const selectedCities = useMemo(() => {
    const set = new Set(filter.cities || []);
    if (filter.city) set.add(filter.city);
    return Array.from(set);
  }, [filter.cities, filter.city]);

  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    if (filter.country) p.set('country', filter.country);
    if (filter.state) p.set('state', filter.state);
    if (selectedCities.length) p.set('city', selectedCities.join(','));
    if (filter.region) p.set('region', filter.region);
    if (filter.date_from) p.set('date_from', filter.date_from);
    if (filter.date_to) p.set('date_to', filter.date_to);
    if (filter.ops_division) p.set('ops_division', filter.ops_division);
    p.set('window_days', String(windowDays));
    p.set('view_as', filter.view_as);
    return p.toString();
  }, [filter, windowDays, selectedCities]);

  useLayoutEffect(() => {
    if (!opts.sharedAdmin) return;
    installAdminScopeTransport();
    setAdminRequestScope({ view_as: filter.view_as, country: filter.country, state: filter.state,
      region: filter.region, city: selectedCities.join(','), ops_division: filter.ops_division,
      date_from: filter.date_from, date_to: filter.date_to, window_days: windowDays, refresh: refreshTick });
  }, [opts.sharedAdmin, filter, selectedCities, windowDays, refreshTick]);
  useLayoutEffect(() => () => { if (opts.sharedAdmin) setAdminRequestScope(null); }, [opts.sharedAdmin]);

  return sharedFilter && !opts.sharedAdmin ? sharedFilter : {
    boardId, filter, setFilter, refresh, refreshTick, clear, loading, catalog, sharedAdmin: opts.sharedAdmin,
    windowDays, queryString, user,
    variant: opts.variant || autoVariant(user),
    // Direct accessors:
    country: filter.country,
    state: filter.state, city: filter.city, region: filter.region,
    cities: selectedCities,
    view_as: filter.view_as, date_from: filter.date_from, date_to: filter.date_to,
    snapshot_period: filter.snapshot_period,
  };
}

// ─────────────────────────────────────────────────────────────
// EXPORT CSV — small client-side snapshot; boards may pass their own
// exporter via the `onExportCsv` prop.
// ─────────────────────────────────────────────────────────────
function exportFilterSnapshotCsv(filter, windowDays, boardId, cities) {
  const rows = [
    ['field', 'value'],
    ['board_id',        boardId],
    ['view_as',         filter.view_as],
    ['country',         filter.country || 'Global'],
    ['snapshot_period', filter.snapshot_period],
    ['window_days',     windowDays],
    ['state',           filter.state || ''],
    ['region',          filter.region || ''],
    ['cities',          (cities || []).join('; ')],
    ['date_from',       filter.date_from || ''],
    ['date_to',         filter.date_to || ''],
    ['exported_at',     new Date().toISOString()],
  ];
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `necs-${boardId}-snapshot-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Statistics-section selection style (Operations HQ): gold fill on active.
const pillStyle = (active, disabled = false) => ({
  backgroundColor: active ? '#C9A962' : 'transparent',
  color: disabled ? '#52525b' : active ? '#0a0a0a' : '#a1a1aa',
  border: `1px solid ${active ? '#C9A962' : '#3f3f46'}`,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.55 : 1,
});

/**
 * The Enterprise Pivot Filter bar. Rendered once per department board,
 * below the department header / role / key responsibilities section.
 */
export default function UnifiedGeoRoleFilter({ filter, testIdPrefix, onExportCsv }) {
  const prefix = testIdPrefix || `unified-filter-${filter?.boardId || 'default'}`;
  if (!filter || filter.loading || !filter.catalog) return null;
  const isField = filter.variant === 'field';
  const { catalog } = filter;
  const regions = (catalog.regions?.[filter.state] || []).filter(r => !filter.filter.cities?.length || filter.filter.cities.includes(r.big_city));
  const cities = (catalog.cities?.[filter.state] || []).filter(c =>
    (!filter.filter.cities?.length || filter.filter.cities.includes(c.big_city) || filter.filter.cities.includes(c.name)) &&
    (!filter.region || c.region_ids?.includes(filter.region)));
  const isCustom = filter.snapshot_period === 'custom';
  const isCanada = filter.filter.country === 'CA';

  const toggleTxCity = (c) => {
    const cur = new Set(filter.filter.cities || []);
    if (cur.has(c)) cur.delete(c); else cur.add(c);
    filter.setFilter({ cities: Array.from(cur) });
  };

  // ── Field variant — compact locked snapshot filter (unchanged behavior) ──
  if (isField) {
    return (
      <div className="w-full rounded-lg mb-3 p-4" style={{ backgroundColor: '#111', border: '1px solid #2D2D2D' }}
           data-testid={prefix} data-variant="field">
        <p className="text-[11px] tracking-wide mb-3 leading-snug" style={{ color: '#a1a1aa' }} data-testid={`${prefix}-header`}>
          Snapshot period for your assigned territory. Filter and export.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" onClick={filter.refresh}
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
                  style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46', color: '#e5e5e5' }}
                  data-testid={`${prefix}-refresh`}>
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          <button type="button"
                  onClick={() => (onExportCsv ? onExportCsv(filter) : exportFilterSnapshotCsv(filter.filter, filter.windowDays, filter.boardId, filter.cities))}
                  className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
                  style={{ backgroundColor: '#C9A962', color: '#0a0a0a', border: '1px solid #C9A962' }}
                  data-testid={`${prefix}-export-csv`}>
            <Download className="h-3 w-3" /> Export CSV
          </button>
          <div className="flex items-center gap-2 pl-2 border-l border-zinc-800">
            <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: '#C9A962' }}>
              <Calendar className="h-3.5 w-3.5" /> Snapshot Period
            </span>
            <select value={filter.snapshot_period}
                    onChange={(e) => filter.setFilter({ snapshot_period: e.target.value })}
                    className="text-xs px-2 py-1 rounded-md bg-zinc-900 border border-zinc-700 text-white"
                    data-testid={`${prefix}-snapshot-period`}>
              {SNAPSHOT_PERIODS.filter((p) => !p.legacy).map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 flex items-center gap-1" data-testid={`${prefix}-locked-hint`}>
          <Lock className="h-3 w-3" /> Locked to your assigned territory.
          <Users className="h-3 w-3 ml-2" />
        </p>
      </div>
    );
  }

  // ── Admin variant — Enterprise Pivot Filter (Rows 1→5) ──
  return (
    <>
    <div className="w-full min-w-0 rounded-lg mb-3 p-3 sm:p-4 space-y-3"
         style={{ backgroundColor: '#111', border: '1px solid #2D2D2D' }}
         data-testid={prefix} data-variant="admin">
      <p className="text-[11px] tracking-wide leading-snug" style={{ color: '#a1a1aa' }} data-testid={`${prefix}-header`}>
        NECS Admin Search
      </p>

      {/* Row 1 — Role selector: Corporate Admin | Valet | Cleaners.
          Iter-112 · Item 1 — the standalone corporate/admin role section
          was retired; Corporate Admin lives here and aggregates BOTH
          service lines. Valet / Cleaners scope exclusively. */}
      <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-role-row`}>
        <span className="text-[10px] uppercase tracking-widest font-bold mr-1" style={{ color: '#C9A962' }}>Role</span>
        <button type="button" onClick={() => filter.setFilter({ view_as: 'corporate' })}
                className="text-xs font-bold px-4 py-1.5 rounded-full"
                style={{
                  backgroundColor: filter.view_as === 'corporate' ? '#FFD60A' : 'transparent',
                  color: filter.view_as === 'corporate' ? '#1a1a1a' : '#FFD60A',
                  border: '1px solid #FFD60A',
                }}
                data-testid={`${prefix}-role-corporate`} data-active={filter.view_as === 'corporate'}>
          Corporate Admin
        </button>
        <button type="button" onClick={() => filter.setFilter({ view_as: 'valet', ops_division: '' })}
                className="text-xs font-bold px-4 py-1.5 rounded-full"
                style={{
                  backgroundColor: filter.view_as === 'valet' ? '#C9A962' : 'transparent',
                  color: filter.view_as === 'valet' ? '#1a1a1a' : '#C9A962',
                  border: '1px solid #C9A962',
                }}
                data-testid={`${prefix}-role-valet`} data-active={filter.view_as === 'valet'}>
          Valet
        </button>
        <button type="button" onClick={() => filter.setFilter({ view_as: 'cleaners', ops_division: '' })}
                className="text-xs font-bold px-4 py-1.5 rounded-full"
                style={{
                  backgroundColor: filter.view_as === 'cleaners' ? '#22d3ee' : 'transparent',
                  color: filter.view_as === 'cleaners' ? '#1a1a1a' : '#22d3ee',
                  border: '1px solid #22d3ee',
                }}
                data-testid={`${prefix}-role-cleaners`} data-active={filter.view_as === 'cleaners'}>
          Cleaners
        </button>
        <span className="text-[10px] text-zinc-400 ml-1" data-testid={`${prefix}-population-label`}>
          {filter.view_as === 'corporate'
            ? 'All properties · Field staff · Residents/Tenants'
            : filter.view_as === 'cleaners' ? '12 commercial types · Cleaners · Tenants A/H + A/U' : 'Multifamily · Valet · Residents A/H + A/U'}
        </span>
      </div>

      {/* Row 2 — Country selector: US | Canada */}
      {filter.view_as === 'corporate' && filter.boardId === 'dept-operations' && <div className="flex flex-wrap items-center gap-2 mb-3" data-testid={`${prefix}-ops-division`}>
        <span className="text-xs text-zinc-400">Operations staffing</span>
        {[['', 'All'], ['office', 'Office'], ['warehouse', 'Warehouse']].map(([key, label]) => <button key={key} type="button" className="text-xs px-3 py-1.5 rounded-md" style={pillStyle((filter.filter.ops_division || '') === key)} onClick={() => filter.setFilter({ ops_division: key })} data-testid={`${prefix}-division-${key || 'all'}`}>{label}</button>)}
      </div>}
      <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-country-row`}>
        <span className="text-[10px] uppercase tracking-widest font-bold mr-1 flex items-center gap-1" style={{ color: '#C9A962' }}>
          <Globe2 className="h-3 w-3" /> Country
        </span>
        {[['', 'Global'], ['US', 'US'], ['CA', 'Canada']].map(([code, label]) => (
          <button key={code} type="button"
                  onClick={() => filter.setFilter({ country: code, state: code === 'US' ? 'TX' : '', region: null, city: null, cities: [] })}
                  className="text-[10px] uppercase tracking-widest font-bold px-3.5 py-1.5 rounded-full"
                  style={pillStyle(filter.filter.country === code)}
                  data-testid={`${prefix}-country-${code ? code.toLowerCase() : 'global'}`}
                  data-active={filter.filter.country === code}>
            {label}
          </button>
        ))}
        {isCanada && (
          <span className="text-[10px] text-zinc-500" data-testid={`${prefix}-canada-note`}>
            Canada expansion — regions unlock at launch.
          </span>
        )}
      </div>

      {/* Row 3 — Refresh · Export CSV · Snapshot Period */}
      <div className="flex flex-wrap items-center gap-2" data-testid={`${prefix}-controls-row`}>
        <button type="button" onClick={filter.refresh}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
                style={{ backgroundColor: '#0f0f0f', border: '1px solid #3f3f46', color: '#e5e5e5' }}
                data-testid={`${prefix}-refresh`}>
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
        <button type="button"
                onClick={() => (onExportCsv ? onExportCsv(filter) : exportFilterSnapshotCsv(filter.filter, filter.windowDays, filter.boardId, filter.cities))}
                className="text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
                style={{ backgroundColor: '#C9A962', color: '#0a0a0a', border: '1px solid #C9A962' }}
                data-testid={`${prefix}-export-csv`}>
          <Download className="h-3 w-3" /> Export CSV
        </button>
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5" style={{ color: '#C9A962' }}>
            <Calendar className="h-3.5 w-3.5" /> Snapshot Period
          </span>
          <select value={filter.snapshot_period}
                  onChange={(e) => filter.setFilter({ snapshot_period: e.target.value, ...(e.target.value !== 'custom' ? { date_from: '', date_to: '' } : {}) })}
                  className="text-xs px-2 py-1 rounded-md bg-zinc-900 border border-zinc-700 text-white"
                  data-testid={`${prefix}-snapshot-period`}>
            {SNAPSHOT_PERIODS.filter((p) => !p.legacy).map((p) => (
              <option key={p.key} value={p.key}>{p.label}</option>
            ))}
          </select>
        </div>
        {/* Iter-112 · Item 3 — named per-user presets (max 10). */}
        <FilterPresets filter={filter} prefix={prefix} />
      </div>

      {/* State precedes big cities; dates stay inline when width permits. */}
      <div className="flex flex-wrap items-end gap-3" data-testid={`${prefix}-bottom-row`}>
        <div className="w-full sm:w-36 min-w-0" data-testid={`${prefix}-state-row`}>
          <label htmlFor={`${prefix}-state`} className="block text-[10px] uppercase font-bold mb-1 text-[#C9A962]">State</label>
          <select id={`${prefix}-state`} value={filter.state || ''} data-testid={`${prefix}-state-select`}
            className="w-full text-xs p-2 rounded-md bg-zinc-900 border border-zinc-700 text-white"
            onChange={e => filter.setFilter({ state: e.target.value, region: null, city: null, cities: [] })}>
            <option value="">All states</option>
            {(isCanada ? (catalog.states || []).filter(s => s.country === 'CA').map(s => [s.code, s.name]) : US_STATES).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap flex-1 items-center gap-1.5 min-w-0 basis-full sm:basis-80" data-testid={`${prefix}-city-tabs`}>
          <span className="w-full text-[10px] uppercase font-bold text-[#C9A962]">Big cities</span>
          {(filter.state === 'TX' ? TX_CITIES : catalog.big_cities?.[filter.state] || []).map(c => <button key={c} type="button" onClick={() => { toggleTxCity(c); filter.setFilter({ city: null, region: null }); }}
            className="text-[10px] font-bold px-2.5 py-1.5 rounded-full" style={pillStyle((filter.filter.cities || []).includes(c))}
            data-testid={`${prefix}-city-tab-${c.toLowerCase().replace(/\s+/g, '-')}`} data-active={(filter.filter.cities || []).includes(c)}>{c}</button>)}
        </div>
        <div className="flex gap-2 flex-wrap min-w-0" data-testid={`${prefix}-date-range`}>
          <label className="text-[10px] text-[#C9A962] min-w-0">Date range · From
            <input type="date" aria-label="Date range from" value={filter.date_from || ''} max={filter.date_to || undefined}
              onChange={e => filter.setFilter({ date_from: e.target.value, snapshot_period: 'custom' })}
              className="block w-32 max-w-full text-xs p-2 rounded-md bg-zinc-900 border border-zinc-700 text-white" data-testid={`${prefix}-date-from`} />
          </label>
          <label className="text-[10px] text-[#C9A962] min-w-0">To
            <input type="date" aria-label="Date range to" value={filter.date_to || ''} min={filter.date_from || undefined}
              onChange={e => filter.setFilter({ date_to: e.target.value, snapshot_period: 'custom' })}
              className="block w-32 max-w-full text-xs p-2 rounded-md bg-zinc-900 border border-zinc-700 text-white" data-testid={`${prefix}-date-to`} />
          </label>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3" data-testid={`${prefix}-territory-row`}>
        <div className="flex-1 min-w-0 basis-40">
          <label className="block text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#C9A962' }}>
            Region
          </label>
          <select value={filter.region || ''}
                  onChange={(e) => filter.setFilter({ region: e.target.value || null })}
                  className="w-full text-xs px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-700 text-white"
                  data-testid={`${prefix}-region`}>
            <option value="">All regions</option>
            {(regions || []).map((r) => (
              <option key={r.region_id || r.region_code || r.name} value={r.region_id || r.region_code || r.name}>
                {r.region_code || r.name}{r.name && r.region_code ? ` · ${r.name}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1 min-w-0 basis-40">
          <label className="block text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#C9A962' }}>
            City
          </label>
          <select value={filter.city || ''}
                  onChange={(e) => filter.setFilter({ city: e.target.value || null })}
                  className="w-full text-xs px-2 py-1.5 rounded-md bg-zinc-900 border border-zinc-700 text-white"
                  data-testid={`${prefix}-city`}>
            <option value="">All cities</option>
            {cities.map((c) => (
              <option key={`${c.big_city || ''}-${c.name}`} value={c.name}>
                {c.name}{c.property_count ? ` · ${c.property_count}` : ''}
              </option>
            ))}
          </select>
        </div>

        <button type="button" onClick={filter.clear}
                className="text-[10px] uppercase tracking-widest font-bold flex items-center gap-1 px-3 py-1.5 rounded-md"
                style={{ color: '#a1a1aa', border: '1px solid #3f3f46', backgroundColor: '#0f0f0f' }}
                data-testid={`${prefix}-clear`}>
          <RotateCcw className="h-3 w-3" /> Clear
        </button>
      </div>

      {isCustom && (
        <p className="text-[10px] text-zinc-500" data-testid={`${prefix}-custom-hint`}>
          Custom range active — snapshot period honors From/To.
        </p>
      )}
    </div>
    {filter.sharedAdmin && <AdminPopulationMetrics filter={filter} prefix={prefix} />}
    </>
  );
}

/**
 * FilterPresets — Iter-112 · Item 3. Named, per-user presets (max 10)
 * saved to the backend; one-tap reapply + delete own presets.
 */
function FilterPresets({ filter, prefix }) {
  const { user } = useAuth();
  const [presets, setPresets] = useState([]);
  const [selected, setSelected] = useState('');
  const [companyDefault, setCompanyDefault] = useState(null);
  const [autoLoadedBoard, setAutoLoadedBoard] = useState(null);
  const isCeo = ['admin', 'company_manager'].includes((user?.role || '').toLowerCase());
  // Section 2 (iter-139) — QA Default is scoped to the department board.
  const savedQaForBoard = useMemo(
    () => presets.find((p) => p.name === 'QA Default' && p.board_id === filter.boardId),
    [presets, filter.boardId],
  );

  const load = useCallback(() => {
    fetch(`${API_BASE}/api/filter-presets`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setPresets(d.presets || []); setCompanyDefault(d.company_default || null); } })
      .catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Section 2 (iter-139) — auto-apply this department's saved QA Default
  // exactly once per board load. If none saved, the pivot loads with no
  // preset (same as prior behaviour).
  useEffect(() => {
    if (filter.sharedAdmin || !savedQaForBoard || autoLoadedBoard === filter.boardId) return;
    if (savedQaForBoard.payload) {
      filter.setFilter({ ...savedQaForBoard.payload });
      setSelected(savedQaForBoard.preset_id);
      setAutoLoadedBoard(filter.boardId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedQaForBoard, filter.boardId]);

  const applyPreset = (presetId) => {
    setSelected(presetId);
    if (presetId === '__company__') {
      if (companyDefault?.payload) {
        filter.setFilter({ ...companyDefault.payload });
        filter.refresh();
      }
      return;
    }
    if (presetId === '__qa_default__') {
      // Section 2 (iter-139) — per-department QA Default. If the current
      // department board has a saved "QA Default" preset, apply that.
      // Otherwise fall back to the shared hardcoded baseline (Corporate
      // Admin · US · TX · Dallas + Houston · This Month).
      if (savedQaForBoard?.payload) {
        filter.setFilter({ ...savedQaForBoard.payload });
        filter.refresh();
        setSelected(savedQaForBoard.preset_id);
        return;
      }
      filter.setFilter({
        view_as: 'corporate',
        country: 'US',
        state: 'TX',
        region: null,
        city: null,
        cities: ['Dallas', 'Houston'],
        snapshot_period: 'this_month',
        date_from: '',
        date_to: '',
      });
      filter.refresh();
      return;
    }
    const p = presets.find((x) => x.preset_id === presetId);
    if (p?.payload) {
      filter.setFilter({ ...p.payload });
      filter.refresh();
    }
  };

  const publishSelected = async () => {
    if (!selected || selected === '__company__') return;
    const res = await fetch(`${API_BASE}/api/filter-presets/${selected}/publish`, {
      method: 'POST', credentials: 'include',
    });
    if (res.ok) load();
    else window.alert('Could not publish preset');
  };

  const saveCurrent = async () => {
    const name = window.prompt('Preset name (e.g. "Dallas Cleaners This Month"):');
    if (!name || !name.trim()) return;
    return _persistPreset(name.trim());
  };

  const saveAsDeptQaDefault = async () => {
    if (!window.confirm(`Save the current pivot as this department's QA Default? You can apply it from Presets.`)) return;
    return _persistPreset('QA Default');
  };

  const _persistPreset = async (name) => {
    const f = filter.filter;
    const res = await fetch(`${API_BASE}/api/filter-presets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        name,
        board_id: filter.boardId,
        payload: {
          view_as: f.view_as, country: f.country, state: f.state,
          cities: f.cities || [], city: f.city, region: f.region,
          snapshot_period: f.snapshot_period, date_from: f.date_from, date_to: f.date_to,
        },
      }),
    });
    if (res.ok) load();
    else {
      const d = await res.json().catch(() => ({}));
      window.alert(typeof d.detail === 'string' ? d.detail : 'Could not save preset');
    }
  };

  const deleteSelected = async () => {
    if (!selected) return;
    await fetch(`${API_BASE}/api/filter-presets/${selected}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
    setSelected('');
    load();
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 min-w-0" data-testid={`${prefix}-presets`}>
      <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#C9A962' }}>Presets</span>
      <select value={selected} onChange={(e) => applyPreset(e.target.value)}
              className="text-xs px-2 py-1 rounded-md bg-zinc-900 border border-zinc-700 text-white max-w-[170px]"
              data-testid={`${prefix}-presets-select`}>
        <option value="">— apply preset —</option>
        <option value="__qa_default__">QA Default</option>
        {companyDefault && (
          <option value="__company__">★ Company Default — {companyDefault.name}</option>
        )}
        {presets.map((p) => <option key={p.preset_id} value={p.preset_id}>{p.name}</option>)}
      </select>
      <button type="button" onClick={saveCurrent}
              className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-md"
              style={{ backgroundColor: '#0f0f0f', border: '1px solid #C9A962', color: '#C9A962' }}
              data-testid={`${prefix}-presets-save`}>
        Save
      </button>
      {/* Section 2 (iter-139) — per-department QA Default save button.
          Persists as name='QA Default' + board_id=<dept>. Auto-loads on
          next visit. Available to all roles that see the pivot board. */}
      <button type="button" onClick={saveAsDeptQaDefault}
              className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-md"
              title={`Save current pivot as this department's QA Default (${filter.boardId || '—'})`}
              style={{ backgroundColor: savedQaForBoard ? '#C9A962' : '#0f0f0f',
                       border: '1px solid #C9A962',
                       color: savedQaForBoard ? '#1a1a1a' : '#C9A962' }}
              data-testid={`${prefix}-presets-save-qa`}>
        {savedQaForBoard ? '★ Update QA' : 'Save as QA'}
      </button>
      {selected && selected !== '__company__' && selected !== '__qa_default__' && (
        <button type="button" onClick={deleteSelected}
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-md"
                style={{ backgroundColor: '#0f0f0f', border: '1px solid #ef4444', color: '#ef4444' }}
                data-testid={`${prefix}-presets-delete`}>
          Delete
        </button>
      )}
      {isCeo && selected && selected !== '__company__' && selected !== '__qa_default__' && (
        <button type="button" onClick={publishSelected}
                className="text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-md"
                style={{ backgroundColor: '#C9A962', border: '1px solid #C9A962', color: '#1a1a1a' }}
                data-testid={`${prefix}-presets-publish`}
                title="Publish as company-wide default preset (visible to all directors)">
          Publish
        </button>
      )}
    </div>
  );
}

/**
 * View as: Valet / Cleaners toggle. Kept for standalone placements; the
 * Enterprise Pivot Filter now embeds this as Row 1.
 */
export function ViewAsToggle({ filter, testIdPrefix = 'view-as-toggle' }) {
  if (!filter) return null;
  return (
    <div className="flex items-center gap-2 mb-3" data-testid={testIdPrefix}>
      <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">View as:</span>
      <button type="button" onClick={() => filter.setFilter({ view_as: 'valet' })}
              className="text-xs font-bold px-3 py-1.5 rounded-full border"
              style={{
                backgroundColor: filter.view_as === 'valet' ? '#C9A962' : 'transparent',
                color: filter.view_as === 'valet' ? '#1a1a1a' : '#C9A962',
                borderColor: '#C9A962',
              }}
              data-testid={`${testIdPrefix}-valet`} data-active={filter.view_as === 'valet'}>
        Valet
      </button>
      <button type="button" onClick={() => filter.setFilter({ view_as: 'cleaners' })}
              className="text-xs font-bold px-3 py-1.5 rounded-full border"
              style={{
                backgroundColor: filter.view_as === 'cleaners' ? '#22d3ee' : 'transparent',
                color: filter.view_as === 'cleaners' ? '#1a1a1a' : '#22d3ee',
                borderColor: '#22d3ee',
              }}
              data-testid={`${testIdPrefix}-cleaners`} data-active={filter.view_as === 'cleaners'}>
        Cleaners
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Legacy shims — kept so existing imports do not break while
 * downstream boards migrate to `useBoardFilter`. All return no-op
 * data so old consumers behave as if the filter were empty.
 * ───────────────────────────────────────────────────────────── */
export function UnifiedFilterProvider({ children }) { return children; }
export function useUnifiedFilter() {
  return {
    filter: DEFAULT_STATE,
    setFilter: () => {},
    clear: () => {},
    refresh: () => {},
    refreshTick: 0,
    loading: false,
    windowDays: 30,
    queryString: '',
    state: null, region: null, city: null, cities: [],
    view_as: 'valet', date_from: '', date_to: '', snapshot_period: 'this_month',
    country: 'US',
    catalog: null,
  };
}
export function isFilterAdmin() { return false; } // no longer used — kept for import compat
